import { BhImage } from 'models/BhImage';
import Image from 'components/Shared/Image';
import styles from './styles.scss';
import cn from 'classnames';
import { rm, NO_ELEMENT } from 'utils';
import { SrcSet } from 'components/Shared/Image/SrcSet';
import ResponsiveImage from 'components/Shared/Image/ResponsiveImage';

interface CategoryLinksProps {
  title?: string;
  image?: BhImage;
  categoryUrl?: string;
  isPopular?: boolean;
  appearance: 'primary' | 'secondary' | 'tertiary' | 'quarternary';
  className?: string;
  titleClassName?: string;
  CustomComponent?: React.ComponentType;
  children?: React.ReactNode;
  handleClick?: () => void;
}

interface TagProps {
  show?: boolean;
}

const MOBILE_WIDTH = 79;
const DESKTOP_WIDTH = 159;

const sizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: MOBILE_WIDTH,
    width: MOBILE_WIDTH,
    height: MOBILE_WIDTH
  },
  medium: {
    viewWidth: DESKTOP_WIDTH,
    width: DESKTOP_WIDTH,
    height: DESKTOP_WIDTH
  }
};

const responsive: ResponsiveImage = {
  srcSet: Object.values(sizes),
  sizes: `(max-width: 768px) ${sizes.default.viewWidth}px,
  ${sizes.medium.viewWidth}px`
};

const CategoryTag = ({ show = false }: TagProps) =>
  show ? (
    <div className={styles.categoryTag}>
      <p>{rm('popular_tag')}</p>
    </div>
  ) : (
    NO_ELEMENT
  );

const CategoryImage = ({
  image,
  title,
  isPopular,
  CustomComponent
}: Pick<CategoryLinksProps, 'image' | 'title' | 'isPopular' | 'CustomComponent'>) => {
  const content = CustomComponent ? (
    <CustomComponent />
  ) : image ? (
    <Image
      responsive={responsive}
      wrapperStyle={{ width: '100%' }}
      paddingBottom
      {...image}
      height={DESKTOP_WIDTH}
      width={DESKTOP_WIDTH}
      alt={title}
    />
  ) : (
    NO_ELEMENT
  );

  return (
    <div className={styles.imageWrapper}>
      <div className={styles.categoryTagImageContainer}>
        <CategoryTag show={isPopular} />
        {content}
      </div>
    </div>
  );
};

export const CategoryLink = ({
  image,
  CustomComponent,
  title,
  categoryUrl,
  isPopular = false,
  appearance,
  className,
  titleClassName,
  children,
  handleClick
}: CategoryLinksProps) => {
  const calcClass = cn(styles.baseWrapper, className, {
    [styles.primary]: appearance === 'primary',
    [styles.secondary]: appearance === 'secondary',
    [styles.tertiary]: appearance === 'tertiary',
    [styles.quarternary]: appearance === 'quarternary'
  });
  const content = (
    <>
      <CategoryImage {...{ image, title, isPopular, CustomComponent }} />
      {title && <p className={cn(styles.title, titleClassName)}>{title}</p>}
      {children}
    </>
  );

  return categoryUrl ? (
    <a href={categoryUrl} className={calcClass} onClick={handleClick}>
      {content}
    </a>
  ) : (
    <span className={calcClass}>{content}</span>
  );
};
